/**
 * @function debounce
 * @param {!Function} func - The function that should be executed with a debounce
 * @param {number=} delay - Time in milliseconds until the function will be fired
 * @param {string=} scope - The context of the fired function
 */
function debounce(func, delay = 250, scope = this) {
  let timer = null

  return function () {
    const ARGS = arguments

    clearTimeout(timer)
    timer = setTimeout(() => func.apply(scope, ARGS), delay)
  }
}

/**
 * @function triggerEvent
 * @param {Object} element - The element where we want to trigger an event
 * @param {string} type - The type of the event
 */
function triggerEvent (element, type) {
  let event

  if (typeof window.CustomEvent === 'function') {
    event = new CustomEvent(type)
  } else {
    /** This is used for old Internet Explorer versions. */
    /** @deprecated */
    event = document.createEvent('CustomEvent')
    event.initCustomEvent(type, false, true, null)
  }

  element.dispatchEvent(event)
}

/**
  * Get current date and time and return parts.
  * @function getCurrentDateAndTime
  */
function getCurrentDateAndTime (useDate) {
  const date = useDate !== undefined ? useDate : new Date()
  const zeroPad = (num, places) => String(num).padStart(places, '0')
  const day = zeroPad(date.getDate(), 2)
  const month = zeroPad(date.getMonth() + 1, 2)
  const year = date.getFullYear()
  const hours = zeroPad(date.getHours(), 2)
  const minutes = zeroPad(date.getMinutes(), 2)

  return {
    day,
    month,
    year,
    time: `${hours}:${minutes}`
  }
}

function getRealtime (time, realTime) {
  const start = time.split(':')
  const end = realTime.split(':')
  const startTime = parseInt(start[0]) * 60 + parseInt(start[1])
  const endTime = parseInt(end[0]) * 60 + parseInt(end[1])
  const hasTimeDifference = realTime !== time
  const minutesDifference = endTime - startTime
  const hasMinutesDifference = hasTimeDifference && minutesDifference !== 0
  const exportTime = hasTimeDifference ? realTime : null

  return {
    exportTime,
    minutesDifference: hasMinutesDifference ? minutesDifference : null
  }
}

function kebabToCamel (str) {
  return str.replace(/-./g, (x) => x[1].toUpperCase())
}

function arrayContentEqualsIgnoreOrder (a, b) {
  if (a.length !== b.length) {
    return false
  }

  const uniqueValues = new Set([...a, ...b])

  for (const v of uniqueValues) {
    const aCount = a.filter((e) => e === v).length
    const bCount = b.filter((e) => e === v).length

    if (aCount !== bCount) {
      return false
    }
  }

  return true
}

function convertRemToPixels (rem) {
  return parseFloat(rem) * parseFloat(getComputedStyle(document.documentElement).fontSize)
}

/**
 * Returns a valid datetime attribute value for time HTMLElements.
 * duration 70min -> PT1H30M
 * duration 1h 10min -> PT1H30M
 * We do not need seconds for our application so we do not mind them.
 * @function getDatetimeDuration
 * @param {Number} minutes
 * @param {Number} hours
 * @param {Number} days
 */
function getDatetimeDuration ({ minutes, hours, days }) {
  let datetime = ''

  /**
   * prepare correct values
   */
  if (minutes) {
    // convert minutes to hours
    if (minutes >= 60) {
      hours = hours ? hours + Math.floor(minutes / 60) : Math.floor(minutes / 60)
      minutes = minutes % 60
    }
  }

  if (hours) {
    // convert hours to days
    if (hours >= 24) {
      days = days ? days + Math.floor(hours / 24) : Math.floor(hours / 24)
      hours = hours % 24
    }
  }

  datetime = 'P'

  if (days) {
    datetime += days + 'D'
  }

  if (hours || minutes) {
    datetime += 'T'

    if (hours) {
      datetime += hours + 'H'
    }

    if (minutes) {
      datetime += minutes + 'M'
    }
  }

  return datetime
}

async function getCurrentLocation () {
  const position = await new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject)
  })

  return {
    longitude: position.coords.longitude,
    latitude: position.coords.latitude
  }
}

/**
 * Gets the text content of the default slot.
 * Should be called with: this.$slots.default && getDefaultSlotTextContent(this.$slots.default())
 * @function getDefaultSlotTextContent
 * @param children
 * @returns {String} text
 */
function getDefaultSlotTextContent (children) {
  let text = children.map((node) => {
    if (!node.children || typeof node.children === 'string') {
      return node.children.trim() || ''
    } else if (Array.isArray(node.children)) {
      return getDefaultSlotTextContent(node.children)
    } else if (node.children.default) {
      return getDefaultSlotTextContent(node.children.default())
    }
  }).join('')

  return text
}

/**
 * Sort points alpabetically.
 * @function getPointsSortedAlphabetically
 * @param {Array} points - points to be sorted
 * @returns
 */
function getPointsSortedAlphabetically (points) {
  if (!points?.length) return []

  return points.toSorted(function(pointA, pointB) {
    return getLabelOfPoint(pointA).toLowerCase().localeCompare(getLabelOfPoint(pointB).toLowerCase())
  });
}

/**
 * Sort connections alpabetically by their origin.
 * TODO: sort also by destination.
 * @function getConnectionsSortedAlphabetically
 * @param {Array} connections - connections to be sorted
 * @returns
 */
function getConnectionsSortedAlphabetically (connections) {
  if (!connections?.length) return []

  return connections.toSorted(function(connectionA, connectionB) {
    return getLabelOfPoint(connectionA.stops.origin).toLowerCase().localeCompare(getLabelOfPoint(connectionB.stops.origin).toLowerCase())
  });
}

/**
 * Get a label that represents the given point object.
 * @function getLabelOfPoint
 * @param {Object} point - a point object
 * @returns {String} label used for identifying the point
 */
function getLabelOfPoint (point) {
  if (!point) {
    return
  }

  const name = point.name
  const place = point.place
  let label = ''

  if (name) {
    label = name

    if (place) {
      label += ', '
      label += place
    }
  } else if (place) {
    label = place
  }

  return label
}

export {
  debounce,
  triggerEvent,
  getCurrentDateAndTime,
  getRealtime,
  kebabToCamel,
  arrayContentEqualsIgnoreOrder,
  convertRemToPixels,
  getDatetimeDuration,
  getCurrentLocation,
  getDefaultSlotTextContent,
  getPointsSortedAlphabetically,
  getConnectionsSortedAlphabetically,
  getLabelOfPoint,
}
